import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { companyLogos } from '../assets/companyLogos';
import AnimatedSection from './AnimatedSection';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const textVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const WorkSection: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <div className="mt-40 md:mt-40 px-4 md:px-8 bg-black mb-20 relative overflow-hidden">
      {/* Background Parallax Effect */}
      <motion.div 
        className="absolute inset-0 opacity-5"
        style={{ y }}
      >
        <div className="w-full h-full"></div>
      </motion.div>

      <div className="max-w-2xl md:max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
        >
          {/* Always visible h2 with enhanced animation */}
          <motion.h2 
            className="text-lg font-bold mb-6 text-white"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            Selected projects & work
          </motion.h2>

          {/* Main heading with text reveal effect */}
          <motion.h1 
            className="text-2xl font-bold leading-[2.3rem] text-left mb-12 md:text-5xl md:font-semibold md:leading-[3.5rem] md:max-w-5xl md:mb-12 md:text-left"
            variants={textVariants}
          >
            <span className="text-reveal">
              Highlighted projects I've led, delivering impactful digital experiences with strategic vision, brought to life in collaboration with a talented team.
            </span>
          </motion.h1>

          {/* Button with magnetic effect */}
          <motion.div 
            className="flex justify-end mb-16"
            variants={itemVariants}
          >
            <motion.a
              href="/background"
              className="group flex items-center gap-2 px-5 py-2 text-base font-medium border border-white/30 rounded-full hover:bg-white/40 transition-all duration-300 shadow-sm md:gap-4 md:px-6 md:py-3 md:text-lg magnetic hover-lift"
              whileHover={{ 
                scale: 1.08, 
                boxShadow: '0 8px 32px rgba(255,255,255,0.15)',
                y: -2
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ 
                type: 'spring', 
                stiffness: 300,
                damping: 20
              }}
            >
              <span className="relative overflow-hidden">
                <span className="relative z-10">Learn about me</span>
                <motion.span 
                  className="absolute inset-0 bg-white/20 rounded-full"
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ originX: 0 }}
                />
              </span>
              <motion.span 
                className="flex items-center justify-center w-8 h-8 border border-white rounded-full ml-2 group-hover:bg-white/10 transition-all duration-300"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </motion.span>
            </motion.a>
          </motion.div>

          {/* Enhanced Logos Grid */}
          <AnimatedSection animation="slideUp" delay={0.3}>
            <div className="px-2">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-12">
                {companyLogos.map((company, index) => (
                  <motion.a
                    key={company.name}
                    href={company.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-16 flex items-center justify-center opacity-90 transition-all duration-300 rounded-lg md:h-24 group relative overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20"
                    variants={itemVariants}
                    whileHover={{ 
                      scale: 1.1, 
                      opacity: 1,
                      y: -5,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderColor: 'rgba(255, 255, 255, 0.4)'
                    }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 0.9, y: 0 }}
                    transition={{ 
                      delay: index * 0.1,
                      duration: 0.6,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                  >
                    {/* Hover Background Effect */}
                    <motion.div 
                      className="absolute inset-0 bg-white/15 rounded-lg"
                      initial={{ scale: 0 }}
                      whileHover={{ scale: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* Logo Container */}
                    <div className="relative z-10 w-full h-full flex items-center justify-center rounded-lg transition-all duration-300 p-2">
                      <motion.img 
                        src={company.logo} 
                        alt={company.name}
                        className="max-h-12 object-contain md:max-h-20 transition-all duration-300 filter brightness-110 contrast-125"
                        whileHover={{ 
                          scale: 1.15,
                          filter: 'brightness(1.4) contrast(1.3)'
                        }}
                        style={{
                          filter: 'brightness(1.1) contrast(1.2) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                        }}
                      />
                    </div>

                    {/* Enhanced Glow Effect */}
                    <motion.div 
                      className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100"
                      style={{
                        background: 'radial-gradient(circle, rgba(255,255,255,0.25) 0%, transparent 70%)',
                        filter: 'blur(8px)'
                      }}
                      initial={{ scale: 0.8 }}
                      whileHover={{ scale: 1.2 }}
                      transition={{ duration: 0.3 }}
                    />

                    {/* Company Name Tooltip */}
                    <motion.div
                      className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-white/90 text-black text-xs font-medium px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-20"
                      initial={{ y: 10 }}
                      whileHover={{ y: 0 }}
                    >
                      {company.name}
                    </motion.div>
                  </motion.a>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkSection;